import React, { useState } from "react";
import { useTheme, CircularProgress, Menu, MenuItem, Typography } from "@mui/material";
import { LAYOUTS, TYPES } from "src/components/widgets/FieldCopyPaste/CopyPaste.consts";
import { StyledIconButton } from "src/components/widgets/FieldCopyPaste/CopyPaste.commons";
import TriggerPlayIcon from "src/icons/player/TriggerPlayIcon";
import TriggerPauseIcon from "src/icons/player/TriggerPauseIcon";
import DotsVerticalIcon from "src/icons/field-actions/DotsVerticalIcon";
import CodeExpressionIcon from "src/icons/field-actions/CodeExpressionIcon";
import RevertIcon from "src/icons/field-actions/RevertIcon";
import Tooltip from "@hyro-components/Tooltip";
import useAppStore from "src/stores/appStore";
import { systemModeSelector } from "src/stores/selectors/appSelectors";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";

const MENU_ITEM_SX = { display: "flex", alignItems: "center", height: 40, p: 1, gap: 1, borderRadius: "4px", minHeight: "unset" };

const FieldActionMenu = ({
  showPlayerButton,
  showExpressionButton,
  isPlaying,
  isLoading,
  onPlayPause,
  onExpressionClick,
  onRevertToDefault,
  onOpenChange,
  isPlayDisabled
}) => {
  const theme = useTheme();
  const systemMode = useAppStore(systemModeSelector);
  const isEditMode = systemMode === SYSTEM_MODES.EDIT;
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    onOpenChange?.(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onOpenChange?.(false);
  };

  const handleItemClick = (callback) => () => {
    callback?.();
    handleClose();
  };

  return (
    <>
      <Tooltip disableInteractive title="More options">
        <StyledIconButton
          onClick={handleOpen}
          size={26}
          type={TYPES.FLAT}
          layout={LAYOUTS.ROUNDED}
          data-testid="field-action-three-dot-menu"
          sx={{ backgroundColor: isOpen ? theme.palette.tertiary[300] : theme.palette.system[0] }}
        >
          <DotsVerticalIcon size={18} style={{ flexShrink: 0, width: "18px", height: "18px" }} />
        </StyledIconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 0.5 }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              borderRadius: "8px",
              boxShadow: theme.shadows[4]
            }
          },
          list: { sx: { p: 0 } }
        }}
      >
        {showPlayerButton && (
          <MenuItem
            onClick={handleItemClick(onPlayPause)}
            disabled={isPlayDisabled}
            data-testid="menu-item-play"
            sx={MENU_ITEM_SX}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: theme.palette.system[700] }} />
            ) : isPlaying ? (
              <TriggerPauseIcon size={20} />
            ) : (
              <TriggerPlayIcon size={20} />
            )}
            <Typography variant="body2">
              {isLoading ? "Loading..." : isPlaying ? "Pause utterance" : "Play utterance"}
            </Typography>
          </MenuItem>
        )}
        {showExpressionButton && (
          <MenuItem
            onClick={handleItemClick(onExpressionClick)}
            data-testid="menu-item-expression"
            sx={MENU_ITEM_SX}
          >
            <CodeExpressionIcon size={20} />
            <Typography variant="body2">
              {isEditMode ? "Custom expression" : "View expression"}
            </Typography>
          </MenuItem>
        )}
        {onRevertToDefault && (
          <MenuItem
            onClick={handleItemClick(onRevertToDefault)}
            data-testid="menu-item-revert"
            sx={MENU_ITEM_SX}
          >
            <RevertIcon size={20} />
            <Typography variant="body2">
              Revert to default
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default FieldActionMenu;
